import React, { useState, useEffect, useRef, useId } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FaBars, FaChevronDown } from 'react-icons/fa';
import { getHamburgerMenuLinks, getBandSongListHamburgerMenuLinks, getBandEventsHamburgerMenuLinks } from '../../config/navigationLinks.js';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import { inlineEndOffset } from '../../_helpers/rtl.js';
import '../../styles/partial/HamburgerMenu.css';

/** Below this width the dropdown is centred instead of anchored to the button. */
export const HAMBURGER_CENTER_BREAKPOINT = 588;

const HamburgerMenu = ({ profileImageURL, bandSpecific = false, canManageBand = false, pageType = 'default', onImportSonglist, onAddEvent }) => {
  const navigate = useNavigate();
  const { t, dir } = useLocale();
  const { bandid } = useParams();
  const dropdownId = useId();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  // Set when the menu was opened with the button (click / Enter / Space) so
  // focus follows into the first item; hover-opening must not steal focus.
  const focusFirstItemRef = useRef(false);

  // Get navigation links from shared config based on page type
  const getNavigationLinks = () => {
    if (bandSpecific) {
      // The band links are all admin actions (Add Song, Import, Edit Tags,
      // Band Settings, Add Event): members only (ENG-500).
      if (!canManageBand) return [];
      switch (pageType) {
        case 'song-list':
          return getBandSongListHamburgerMenuLinks();
        case 'events':
          return getBandEventsHamburgerMenuLinks();
        default:
          return [];
      }
    }
    return getHamburgerMenuLinks();
  };

  const navigationLinks = getNavigationLinks();

  // Check sessionStorage if profileImageURL is not passed as a prop
  const currentProfileImageURL = profileImageURL || sessionStorage.getItem('ProfileImageURL');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        // Check if click is inside dropdown
        const dropdownElement = dropdownRef.current;
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setIsDropdownVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clear any pending timeout when component unmounts
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Window resize listener for responsive positioning
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // The dropdown is portaled to <body>, so it is not next to the trigger in
  // DOM order. Opening it from the button moves focus onto the first item
  // so Tab walks the items next; Escape hands focus back to the button.
  useEffect(() => {
    if (!isDropdownVisible || !focusFirstItemRef.current) return;
    focusFirstItemRef.current = false;
    dropdownRef.current?.querySelector('.hamburger-dropdown-item')?.focus();
  }, [isDropdownVisible]);

  const handleButtonClick = () => {
    focusFirstItemRef.current = !isDropdownVisible;
    setIsDropdownVisible(!isDropdownVisible);
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsDropdownVisible(true);
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsDropdownVisible(false);
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    setIsDropdownVisible(false);
  };

  const closeAndRefocus = () => {
    setIsDropdownVisible(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isDropdownVisible) {
      event.preventDefault();
      closeAndRefocus();
    }
  };

  // Tab / Shift+Tab out of the menu (or a click that moves focus elsewhere) closes it.
  const handleDropdownBlur = (event) => {
    const next = event.relatedTarget;
    if (next && (dropdownRef.current?.contains(next) || buttonRef.current?.contains(next))) return;
    setIsDropdownVisible(false);
  };

  const handleLinkClick = (link) => {
    if (link.isAction) {
      if (link.id === 'import-songlist' && onImportSonglist) {
        // Handle special action for import songlist
        onImportSonglist();
      } else if (link.id === 'add-event' && onAddEvent) {
        // Handle special action for add event
        onAddEvent();
      }
    } else {
      // Handle regular navigation
      let path = link.path;
      if (link.bandSpecific && bandid && path.includes(':bandid')) {
        path = path.replace(':bandid', bandid);
      }
      navigate(path, link.state ? { state: link.state } : undefined);
    }
    setIsDropdownVisible(false);
  };

  const renderNavigationItem = (link) => {
    if (link.requiresProfileImage) {
      return (
        <>
          {currentProfileImageURL && (
            <img
              src={currentProfileImageURL}
              alt={t('navigation.profileImageAlt')}
              className="hamburger-dropdown-profile-image"
            />
          )}
          <span className="hamburger-dropdown-label">{t(link.labelKey)}</span>
        </>
      );
    }

    return (
      <>
        <span className="hamburger-dropdown-label">{t(link.labelKey)}</span>
      </>
    );
  };

  // A menu with nothing in it is not offered (ENG-563): the band menu has no
  // links until the viewer's role has loaded, and none for a viewer who can't
  // manage the band, and it used to open as an empty box. It appears once
  // there is something to show.
  if (navigationLinks.length === 0) return null;

  const centered = windowWidth <= HAMBURGER_CENTER_BREAKPOINT;

  return (
    <>
      <button
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        className="top-page-nav-button hamburger-menu-button"
        data-testid="hamburger-menu-button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={buttonRef}
        aria-label={t('navigation.menuAccessibleName')}
        aria-expanded={isDropdownVisible}
        aria-controls={isDropdownVisible ? dropdownId : undefined}
      >
        <FaBars aria-hidden="true" />
        {t('navigation.menu')}
        <FaChevronDown className="dropdown-arrow" aria-hidden="true" />
      </button>

      {isDropdownVisible && buttonRect && createPortal(
        <div
          id={dropdownId}
          ref={dropdownRef}
          className={`hamburger-dropdown${centered ? ' hamburger-dropdown--centered' : ''}`}
          data-testid="hamburger-dropdown"
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
          onKeyDown={handleKeyDown}
          onBlur={handleDropdownBlur}
          // Placement goes through custom properties consumed by logical
          // `top` / `inset-inline-end` in HamburgerMenu.css: the menu's
          // inline-end edge lines up with the button's in either direction,
          // and centring (mobile) is direction-neutral.
          style={{
            '--hamburger-dropdown-top': `${buttonRect.bottom + 5}px`,
            '--hamburger-dropdown-inline-end': `${inlineEndOffset(dir, buttonRect, windowWidth)}px`,
          }}
        >
          {navigationLinks.map(link => (
            <button
              key={link.id}
              type="button"
              className="hamburger-dropdown-item"
              data-testid="hamburger-item"
              onClick={() => handleLinkClick(link)}
            >
              {renderNavigationItem(link)}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default HamburgerMenu;
